import { useRef, useEffect, useState, useCallback } from 'react';
import { useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';

/**
 * Hook for scroll-based parallax effects
 * Elements move at different speeds creating depth illusion
 */
export function useScrollParallax(
  speed: number = 0.5,
  offset: ['start' | 'center' | 'end', 'start' | 'center' | 'end'] = ['start', 'end']
) {
  const ref = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: [`${offset[0]} end`, `${offset[1]} start`],
  });

  // Transform scroll progress to Y movement
  // Negative speed = moves up as you scroll down (appears further away)
  // Positive speed = moves down as you scroll down (appears closer)
  const y = useTransform(scrollYProgress, [0, 1], [speed * -100, speed * 100]);
  
  // Add spring physics for smooth movement
  const smoothY = useSpring(y, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return { ref, y: smoothY, progress: scrollYProgress };
}

/**
 * Hook for mouse-tracking parallax effects
 * Elements subtly shift based on cursor position
 */
export function useMouseParallax(sensitivity: number = 0.02) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animation
  const springConfig = { stiffness: 150, damping: 20, restDelta: 0.001 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Calculate mouse position relative to center of screen
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      
      // Normalize to -1 to 1 range, then apply sensitivity
      const normalizedX = ((e.clientX - centerX) / centerX) * sensitivity * 100;
      const normalizedY = ((e.clientY - centerY) / centerY) * sensitivity * 100;
      
      mouseX.set(normalizedX);
      mouseY.set(normalizedY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [sensitivity, mouseX, mouseY]);

  return { x, y };
}

/**
 * Hook for element-local mouse tracking (for tilt cards)
 * Returns rotation values based on mouse position over the element
 */
export function useTilt(maxTilt: number = 15) {
  const ref = useRef<HTMLDivElement>(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const scale = useMotionValue(1);
  
  // Smooth spring for natural feel
  const springConfig = { stiffness: 300, damping: 25, restDelta: 0.001 };
  const smoothRotateX = useSpring(rotateX, springConfig);
  const smoothRotateY = useSpring(rotateY, springConfig);
  const smoothScale = useSpring(scale, { stiffness: 400, damping: 30 });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate rotation based on mouse position
    // rotateY: left/right tilt (positive mouse X = positive rotation)
    // rotateX: up/down tilt (positive mouse Y = negative rotation for natural feel)
    const percentX = (e.clientX - centerX) / (rect.width / 2);
    const percentY = (e.clientY - centerY) / (rect.height / 2);
    
    rotateY.set(percentX * maxTilt);
    rotateX.set(-percentY * maxTilt);
    scale.set(1.02);
  }, [maxTilt, rotateX, rotateY, scale]);

  const handleMouseLeave = useCallback(() => {
    rotateX.set(0);
    rotateY.set(0);
    scale.set(1);
  }, [rotateX, rotateY, scale]);

  return {
    ref,
    rotateX: smoothRotateX,
    rotateY: smoothRotateY,
    scale: smoothScale,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
  };
}

/**
 * Hook for floating animation with subtle parallax
 * Combines ambient floating with mouse response
 */
export function useFloat(
  amplitude: number = 10,
  duration: number = 3,
  delay: number = 0
) {
  const [floatY, setFloatY] = useState(0);
  const { x: mouseX, y: mouseY } = useMouseParallax(0.01);

  useEffect(() => {
    let animationId: number;
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      
      // Sine wave for smooth floating
      const y = Math.sin((elapsed + delay) * (Math.PI * 2) / duration) * amplitude;
      setFloatY(y);
      
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [amplitude, duration, delay]);

  return { floatY, mouseX, mouseY };
}

/**
 * Hook to get global scroll progress (0-1)
 */
export function useGlobalScroll() {
  const [progress, setProgress] = useState(0);
  const smoothProgress = useMotionValue(0);
  const springProgress = useSpring(smoothProgress, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const rawProgress = scrollHeight > 0 ? window.scrollY / scrollHeight : 0;
      const clamped = Math.min(Math.max(rawProgress, 0), 1);
      
      smoothProgress.set(clamped);
      setProgress(clamped);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [smoothProgress]);

  return { progress, springProgress };
}
